/**
 * @packageDocumentation
 * @module      CorPageEntityMaintenance.ts
 * @description Generic page for maintaining an entity. Consists of a list on the left and a card on the right.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement, internalProperty, property } from 'lit-element';
import { universalCardStyles, universalFooterStyles, universalListStyles, universalStyles } from "./CorMain";
import ValidationTracker from "./ValidationTracker";
import "./CorSystemState";
import CloudApiErrorEvent from "./CloudApiErrorEvent";
import { CorThemeSelector } from "./CorThemeSelector";
export var CardStatus;
(function (CardStatus) {
    CardStatus[CardStatus["Adding"] = 0] = "Adding";
    CardStatus[CardStatus["Viewing"] = 1] = "Viewing";
    CardStatus[CardStatus["Editing"] = 2] = "Editing";
    CardStatus[CardStatus["Managing"] = 3] = "Managing";
    CardStatus[CardStatus["Deleting"] = 4] = "Deleting";
    CardStatus[CardStatus["Hidden"] = 5] = "Hidden";
})(CardStatus || (CardStatus = {}));
export class CorPageEntityMaintenance extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The primary key for the item that the user has selected from the list, in string form.
         * Null if adding a new item, undefined if not doing anything yet.
         * The itemId is a string for Systems, a number for everything else. It is the responsibility of derived
         * classes to convert to number if necessary.
         */
        this.itemId = undefined;
        /** True to show a card; false not to. */
        this.cardStatus = CardStatus.Hidden;
        this.showPopover = false;
        this.popoverTitle = '';
        this.popoverText = '';
        this.popoverIsSuccess = true;
        this.popoverIsError = false;
    }
    static get styles() {
        return css `
          ${universalStyles}
          ${universalFooterStyles}
          
          #list {
            width: 50%;
          }

          #card {
            width: 100%;
          }
          
          #RightCardWrapper {
            width: 100%;
            height: 100%;
          }
          
          #BlankCardArea {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;            
          }
          
          #BlankCardArea img {
            opacity: 20%;
          }
          
          #Logo {
            height: 20%;
          }

          sh-popover {
            right: 20px;
            height: 225px;
            width: 400px;
          }
        `;
    }
    get currentTheme() {
        return CorThemeSelector.getTheme();
    }
    /**
     * The list has refreshed and now we must refresh the card for the currently selected item in the list,
     * or hide the card if that item is gone.
     * @private
     */
    onCardRefreshRequested(e) {
        var _a, _b;
        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(this.selectorForCard)) === null || _b === void 0 ? void 0 : _b.dispatchEvent(new CardRefreshRequestedEvent(e.haveData, true));
    }
    setCardStatus(e) {
        var _a, _b;
        this.cardStatus = e.cardStatus;
        // Tell the list that the card status has changed so it knows whether the user should be able to
        // select a new row.
        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(this.selectorForList)) === null || _b === void 0 ? void 0 : _b.dispatchEvent(new CardStatusChangedEvent(this.cardStatus, true));
    }
    conditionallySetItemId(e) {
        if (this.cardStatus === CardStatus.Hidden || this.cardStatus === CardStatus.Viewing) {
            this.itemId = e.getItemId();
            this.cardStatus = CardStatus.Viewing;
        }
    }
    addItem() {
        this.itemId = null;
        this.cardStatus = CardStatus.Adding;
        this.dispatchEvent(new CardStatusChangedEvent(this.cardStatus));
    }
    /**
     * Return true if it's OK to refresh the list and the selected item; false if not.
     * @protected
     */
    get okToRefresh() {
        return this.cardStatus === CardStatus.Hidden || this.cardStatus === CardStatus.Viewing;
    }
    /**
     * Return true if it's OK to add a new item; false if not.
     * @protected
     */
    get okToAdd() {
        return this.cardStatus === CardStatus.Hidden || this.cardStatus === CardStatus.Viewing;
    }
    hidePopover() {
        this.showPopover = false;
    }
    onItemAddedOrUpdated(e) {
        var _a, _b;
        const itemAddedOrUpdatedEvent = e;
        // Capture itemId if it was an add. Having done this, if we press Add again and therefore set itemId=null,
        // it will be a change and the card will be updated.
        this.itemId = itemAddedOrUpdatedEvent.itemIdAsString;
        // Current specs do not hide the card after add or update.
        //this.cardStatus = CardStatus.Hidden;
        // Send a new event to children. The list is listening for this event and will update.
        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(this.selectorForList)) === null || _b === void 0 ? void 0 : _b.dispatchEvent(new ItemAddedOrUpdatedEvent(itemAddedOrUpdatedEvent.itemIdAsString, itemAddedOrUpdatedEvent.isAdd, true));
    }
    /**
     * Tell the list ot refresh itself, maintaining the same selection if possible.
     */
    refreshList( /*e: Event*/) {
        var _a, _b;
        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(this.selectorForList)) === null || _b === void 0 ? void 0 : _b.dispatchEvent(new ListRefreshRequestedEvent());
    }
    onShowPopover(e) {
        const event = e;
        this.popoverTitle = event.getTitle();
        this.popoverText = event.getText();
        this.popoverIsSuccess = event.isSuccess;
        this.popoverIsError = event.isError;
        this.showPopover = true;
        if (this.popoverIsSuccess) {
            setTimeout(() => {
                this.showPopover = false;
            }, 5000);
        }
    }
    onItemDeleted( /*e: Event*/) {
        var _a, _b;
        this.itemId = undefined;
        this.cardStatus = CardStatus.Hidden;
        // Send a new event to children. The list is listening for this event and will update.
        (_b = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector(this.selectorForList)) === null || _b === void 0 ? void 0 : _b.dispatchEvent(new ItemDeletedEvent(true));
    }
    onCloudApiError(e) {
        this.dispatchEvent(new ShowPopoverEvent("Server Error", e.detail, false));
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(CardRefreshRequestedEvent.getEventName(false), this.onCardRefreshRequested);
        this.addEventListener(CardStatusChangedEvent.getEventName(false), this.setCardStatus);
        this.addEventListener(ItemChosenEvent.eventName, this.conditionallySetItemId);
        this.addEventListener(ItemAddedOrUpdatedEvent.getEventName(false), this.onItemAddedOrUpdated);
        this.addEventListener(ItemDeletedEvent.getEventName(false), this.onItemDeleted);
        this.addEventListener(ShowPopoverEvent.getEventName(), this.onShowPopover);
        // @ts-ignore Wants a generic Event, not custom CloudApiErrorEvent
        this.addEventListener(CloudApiErrorEvent.eventName, this.onCloudApiError);
    }
    disconnectedCallback() {
        // @ts-ignore Wants a generic Event, not custom CloudApiErrorEvent
        this.removeEventListener(CloudApiErrorEvent.eventName, this.onCloudApiError);
        this.removeEventListener(ShowPopoverEvent.getEventName(), this.onShowPopover);
        this.removeEventListener(ItemDeletedEvent.getEventName(false), this.onItemDeleted);
        this.removeEventListener(ItemAddedOrUpdatedEvent.getEventName(false), this.onItemAddedOrUpdated);
        this.removeEventListener(ItemChosenEvent.eventName, this.conditionallySetItemId);
        this.removeEventListener(CardStatusChangedEvent.getEventName(false), this.setCardStatus);
        this.removeEventListener(CardRefreshRequestedEvent.getEventName(false), this.onCardRefreshRequested);
        super.disconnectedCallback();
    }
    /**
     * Get an HTML fragment that can be inserted in the rendered page.
     */
    get blankCardHtml() {
        return html `
            <div id="BlankCardArea">
                <img color="secondary" id="Logo" src="./assets/img/${this.blankCardImageFile}" />
                <sh-text color="secondary">No ${this.singleItemName} selected.</sh-text>
                <sh-text color="secondary">Select a ${this.singleItemName} on the table to see detailed information.</sh-text>
            </div>`;
    }
    /**
     * Get an HTML fragment that displays a popover.
     */
    get popoverHtml() {
        return html `
            <sh-popover label="${this.popoverTitle}" passive position="top-right" target="ThePage"
                        ?sticky=${this.popoverIsError}
                        type="${this.popoverIsSuccess ? "confirmation" :
            this.popoverIsError ? "error" :
                ''}"
                        ?visible=${this.showPopover}>
            <sh-icon icon="cancel" size="s" button slot="functions" @click="${() => this.hidePopover()}"></sh-icon>
                <sh-text>${this.popoverText}</sh-text>
            </sh-popover>`;
    }
}
__decorate([
    property()
], CorPageEntityMaintenance.prototype, "itemId", void 0);
__decorate([
    property()
], CorPageEntityMaintenance.prototype, "currentTheme", null);
__decorate([
    internalProperty()
], CorPageEntityMaintenance.prototype, "cardStatus", void 0);
__decorate([
    internalProperty()
], CorPageEntityMaintenance.prototype, "showPopover", void 0);
__decorate([
    internalProperty()
], CorPageEntityMaintenance.prototype, "popoverTitle", void 0);
__decorate([
    internalProperty()
], CorPageEntityMaintenance.prototype, "popoverText", void 0);
__decorate([
    internalProperty()
], CorPageEntityMaintenance.prototype, "popoverIsSuccess", void 0);
__decorate([
    internalProperty()
], CorPageEntityMaintenance.prototype, "popoverIsError", void 0);
/**
 * The list of items being maintained
 *
 * Designed to be a child of a class derived from CorPageItemMaintenance.
 *
 * TSummary - The type of a "summary" of an item being maintained. Example: RoboticNetworkSummary
 */
export class CorPageEntityMaintenanceList extends LitElement {
    constructor() {
        super();
        // Maps the itemId, possibly converted to a string, to the HTML ID of that item's row in the list.
        this.databaseIdToHtmlId = new Map();
        this.itemsList = [];
        /**
         * The ID of the currently selected ("active" in Shui parlance) item.
         * Because this is an HTML attribute of this control, "undefined" comes through as a string,
         * not JavaScript's undefined value.
         */
        this.activeItemId = "undefined";
        this.lastAddedOrUpdatedId = undefined;
        /**
         * Tell when it is OK for the user to select a new item in the list of entities.
         * It will be OK when, for example, the user is merely viewing an item.
         * It will not be OK when, for example, the user is editing an item.
         */
        this.okToSelectNewItemInList = true;
        /**
         * The HTML ID to scroll into view on the next update().
         */
        this.idToScrollToOnNextUpdate = undefined;
        this.initialUpdateComplete = this.getItemSummaries().then(() => {
            return this.requestUpdate();
        });
    }
    static get styles() {
        return css `
          ${universalListStyles}
        `;
    }
    get items() {
        return this.itemsList;
    }
    // See comments on the activeItemId property.
    get haveActiveItem() {
        return this.activeItemId !== "undefined";
    }
    getItemSummaries() {
        return this.getSummaries()
            .then((result) => {
            this.databaseIdToHtmlId.clear();
            this.itemsList = result;
            this.itemsList.sort(this.summarySortCompare);
            this.itemsList.forEach((item, index) => {
                this.databaseIdToHtmlId.set(this.getItemIdAsStringFromSummary(item), "list_item_" + index);
            });
        });
    }
    handleCardStatusChangedEvent(e) {
        const cardStatus = e.cardStatus;
        this.okToSelectNewItemInList = cardStatus === CardStatus.Hidden || cardStatus === CardStatus.Viewing;
        this.requestUpdate();
    }
    handleAddedOrUpdatedEvent(e) {
        const itemAddedOrUpdatedEvent = e;
        this.getItemSummaries().then(() => {
            this.lastAddedOrUpdatedId = itemAddedOrUpdatedEvent.itemIdAsString;
            this.idToScrollToOnNextUpdate = this.lastAddedOrUpdatedId;
            setTimeout(() => this.lastAddedOrUpdatedId = undefined, 500);
        });
    }
    /** Refresh the list and ensure that the same item gets highlighted, if it still exists. */
    handleListRefreshRequestedEvent( /*e: Event*/) {
        const activeItemIdToPreserve = this.haveActiveItem ? this.activeItemId : undefined;
        this.getItemSummaries().then(() => {
            this.idToScrollToOnNextUpdate = activeItemIdToPreserve;
            const stillHaveActiveItem = this.itemsList.some((item) => {
                return this.getItemIdAsStringFromSummary(item) == activeItemIdToPreserve;
            });
            this.dispatchEvent(new CardRefreshRequestedEvent(stillHaveActiveItem, false));
            this.requestUpdate();
        });
    }
    handleDeletedEvent( /*e: Event*/) {
        this.getItemSummaries().then(() => {
            this.lastAddedOrUpdatedId = undefined;
            this.requestUpdate(); // Don't know why we must do this but without it the list does not refresh.
        });
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(ItemAddedOrUpdatedEvent.getEventName(true), this.handleAddedOrUpdatedEvent);
        this.addEventListener(CardStatusChangedEvent.getEventName(true), this.handleCardStatusChangedEvent);
        this.addEventListener(ItemDeletedEvent.getEventName(true), this.handleDeletedEvent);
        this.addEventListener(ListRefreshRequestedEvent.eventName, this.handleListRefreshRequestedEvent);
    }
    disconnectedCallback() {
        this.removeEventListener(ListRefreshRequestedEvent.eventName, this.handleListRefreshRequestedEvent);
        this.removeEventListener(ItemDeletedEvent.getEventName(true), this.handleDeletedEvent);
        this.removeEventListener(CardStatusChangedEvent.getEventName(true), this.handleCardStatusChangedEvent);
        this.removeEventListener(ItemAddedOrUpdatedEvent.getEventName(true), this.handleAddedOrUpdatedEvent);
        super.disconnectedCallback();
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (this.idToScrollToOnNextUpdate) {
            const htmlId = this.databaseIdToHtmlId.get(this.idToScrollToOnNextUpdate);
            this.idToScrollToOnNextUpdate = undefined;
            if (htmlId !== undefined) { // Could be undefined if doing a refresh and the item has been deleted.
                // Without this setTimeout to wait for the next turn of of the cycle, an added one that sorts to the
                // bottom of the list does not become visible.
                setTimeout(() => {
                    var _a;
                    const row = (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.getElementById(htmlId);
                    row === null || row === void 0 ? void 0 : row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }, 0);
            }
        }
    }
}
__decorate([
    property({ attribute: false, type: Array })
], CorPageEntityMaintenanceList.prototype, "items", null);
__decorate([
    property()
], CorPageEntityMaintenanceList.prototype, "activeItemId", void 0);
__decorate([
    property()
], CorPageEntityMaintenanceList.prototype, "lastAddedOrUpdatedId", void 0);
__decorate([
    property()
], CorPageEntityMaintenanceList.prototype, "okToSelectNewItemInList", void 0);
/**
 * An entity that is being added or updated.
 *
 * Designed to be a child of a class that implements CorPageEntityList.
 *
 * TItem - The type of entity being maintained. Example: RoboticNetwork
 */
export class CorPageEntityMaintenanceCard extends LitElement {
    constructor() {
        super();
        this.self = this;
        this.workItem = this.constructItem();
        this.validationTracker = new ValidationTracker();
        this.itemId = undefined;
        this.cardStatus = CardStatus.Hidden;
        this.itemFetchAndUpdateComplete = Promise.resolve();
    }
    static get entityMaintenanceCardStyles() {
        return css `
            ${universalStyles}
            ${universalFooterStyles}
            ${universalCardStyles}

            .no-display {
              display: none;
            }
          
            #CardFooter {
                justify-content: space-between;
                align-items: flex-end;
                width: 100%;
            }
            
            #LeftButtons {
                width: 100%;
                display: flex;
            }    
            #RightButtons {
                display: flex;
            }
            `;
    }
    /**
     * Returns an sh-modal HTML fragment
     */
    get deleteItemModalHtml() {
        return html `
           <sh-modal label='Do you want to delete the ${this.genericItemName}?'
                     sticky
                     modal-height="250px"
                     modal-width="550px"
                     type="alert"
                     ?visible=${this.deleting}>
               <div>
                   ${this.getDeleteBody()}
               </div>
               <sh-button slot="footer" color="tertiary" label="Cancel" @click=${this.cancelDelete}></sh-button>
               <sh-button slot="footer" color="secondary" label="Delete" 
                      @click=${this.delete}></sh-button>
           </sh-modal>`;
    }
    get adding() {
        return this.cardStatus === CardStatus.Adding;
    }
    get viewing() {
        return this.cardStatus === CardStatus.Viewing;
    }
    get editing() {
        return this.cardStatus === CardStatus.Editing;
    }
    get deleting() {
        return this.cardStatus === CardStatus.Deleting;
    }
    get managing() {
        return this.cardStatus === CardStatus.Managing;
    }
    /** Tell whether we are in a state of changing the database-bound data in any way (add, modify, or delete). */
    get changingData() {
        return this.adding || this.deleting || this.managing || this.editing;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(CardRefreshRequestedEvent.getEventName(true), this.handleCardRefreshRequested);
    }
    disconnectedCallback() {
        this.removeEventListener(CardRefreshRequestedEvent.getEventName(true), this.handleCardRefreshRequested);
        super.disconnectedCallback();
    }
    handleCardRefreshRequested(e) {
        if (e.haveData) {
            this.setWorkItemBasedOnItemId();
        }
        else {
            this.changeCardStatus(CardStatus.Hidden);
        }
    }
    changeCardStatus(newStatus) {
        this.cardStatus = newStatus;
        this.dispatchEvent(new CardStatusChangedEvent(newStatus));
    }
    getDeleteBody() {
        return html `<sh-text>${this.genericItemNameForTitle}: ${this.currentItemName}</sh-text>`;
    }
    initiateDelete() {
        this.changeCardStatus(CardStatus.Deleting);
    }
    cancelDelete() {
        this.changeCardStatus(CardStatus.Viewing);
    }
    delete() {
        this.deleteItem(this.workItem).then((response) => {
            if (response === null || response === void 0 ? void 0 : response.ok) {
                this.dispatchEvent(new ItemDeletedEvent());
                this.dispatchEvent(new ShowPopoverEvent(this.genericItemNameForTitle + " Deleted", this.genericItemNameForTitle + " " + this.currentItemName + " has been deleted."));
                this.changeCardStatus(CardStatus.Hidden);
            }
            else {
                this.changeCardStatus(CardStatus.Viewing);
            }
        });
    }
    inputHelper(prop) {
        const self = this;
        return function (e) {
            if (e.currentTarget instanceof Element) {
                // @ts-ignore implicit type of any
                self[prop] = e.currentTarget.getAttribute('value') || '';
            }
        };
    }
    setterBody(newValue, propName, targetObj, targetProp) {
        // @ts-ignore Element implicitly has 'any' type.
        const oldValue = this[propName];
        // @ts-ignore Element implicitly has 'any' type.
        targetObj[targetProp || propName] = newValue;
        this.requestUpdate(propName, oldValue);
    }
    /**
     * Get a short version of the genericItemName consisting of only the last word.
     * In particular, "robotic network" becomes "network".
     */
    get genericItemShortName() {
        return this.genericItemName.replace(/([^ ]* )/g, '');
    }
    static titleCase(s) {
        // Thanks to https://stackoverflow.com/questions/4068573/convert-string-to-pascal-case-aka-uppercamelcase-in-javascript
        return s.replace(/(\w)(\w*)/g, function (g0, g1, g2) { return g1.toUpperCase() + g2.toLowerCase(); });
    }
    get genericItemNameForTitle() {
        return CorPageEntityMaintenanceCard.titleCase(this.genericItemName);
    }
    updated(changedProperties) {
        if (changedProperties.has('itemId')) {
            this.cardStatus = (this.itemId == null || this.itemId == undefined) ? CardStatus.Adding : CardStatus.Viewing;
            this.setWorkItemBasedOnItemId();
        }
    }
    cancelAddOrEdit() {
        this.setWorkItemBasedOnItemId().then(() => {
            this.changeCardStatus(this.adding ? CardStatus.Hidden : CardStatus.Viewing);
        });
    }
    setWorkItemBasedOnItemId() {
        this.workItem = this.constructItem();
        if (this.itemId) {
            return this.itemFetchAndUpdateComplete = this.getItem(this.itemId)
                .then((itemInDatabase) => {
                if (itemInDatabase) {
                    this.workItem = itemInDatabase;
                    return this.requestUpdate();
                }
            });
        }
        else {
            this.itemFetchAndUpdateComplete = Promise.resolve();
            return this.requestUpdate();
        }
    }
}
__decorate([
    property({
        converter: {
            // @ts-ignore 'this' possibly undefined
            fromAttribute: (attrValue) => (attrValue == "null") ? null : attrValue,
            toAttribute: (itemId) => itemId.toString()
        }
    })
], CorPageEntityMaintenanceCard.prototype, "itemId", void 0);
__decorate([
    internalProperty()
], CorPageEntityMaintenanceCard.prototype, "cardStatus", void 0);
/**
 * An event to raise when the user chooses an item from the list.
 */
export class ItemChosenEvent extends CustomEvent {
    constructor(itemId) {
        super(ItemChosenEvent.eventName, {
            bubbles: true,
            composed: true,
            detail: "" + itemId
        });
    }
    getItemId() {
        return this.detail;
    }
}
ItemChosenEvent.eventName = "itemChosen";
/**
 * An event to raise when the status of the card changes
 */
export class CardStatusChangedEvent extends CustomEvent {
    static getEventName(sendingBackDown) {
        return `cardStatusChangedEvent_${sendingBackDown ? 'down' : 'up'}`;
    }
    get cardStatus() {
        return this.detail;
    }
    /**
     * Constructor
     *
     * @param cardStatus The new card status
     * @param sendingBackDown False when raising the event to bubble up through the DOM,
     * true to send it back down. Typical example: raise in the card, handle in the page, send
     * back down to the list.
     */
    constructor(cardStatus, sendingBackDown = false) {
        super(CardStatusChangedEvent.getEventName(sendingBackDown), {
            bubbles: !sendingBackDown,
            composed: !sendingBackDown,
            detail: cardStatus
        });
    }
}
/**
 * An event to raise when the user has requested that the list be refreshed.
 */
class ListRefreshRequestedEvent extends CustomEvent {
    constructor() {
        super(ListRefreshRequestedEvent.eventName, {
            bubbles: false,
            composed: false,
            detail: undefined
        });
    }
}
ListRefreshRequestedEvent.eventName = "ListRefreshRequestedEvent";
/**
 * An event to raise when the card's data should be retrieved anew from the database, based on its current itemId,
 * or we know the card's underlying data have been deleted and we want to hide the card.
 * The list raises this event, sending it up the DOM to the page, and then the page's event handler sends it
 * back down the DOM to the card.
 */
class CardRefreshRequestedEvent extends CustomEvent {
    static getEventName(sendingBackDown) {
        return `CardRefreshRequestedEvent${sendingBackDown ? 'down' : 'up'}`;
    }
    /**
     * True if we want to fetch the card's data and refresh its appearance; false to hide the card.
     */
    get haveData() {
        return this.detail;
    }
    /**
     * Constructor.
     * @param haveData Set to true if there should still be data for the card and we want to fetch it;
     * set to false if we know the record has been deleted and we want to hide the card.
     * @param sendingBackDown Set to false when sending from the list up to the page,
     // and to true when the page sends an event back down to the card.
     */
    constructor(haveData, sendingBackDown = false) {
        super(CardRefreshRequestedEvent.getEventName(sendingBackDown), {
            bubbles: !sendingBackDown,
            composed: true,
            detail: haveData
        });
    }
}
/**
 * The detail object for an ItemAddedOrUpdatedEvent.
 */
export class ItemAddedOrUpdatedEventDetail {
    constructor(itemId, isAdd) {
        this.itemIdAsString = "" + itemId;
        this.isAdd = isAdd;
    }
}
/**
 * An event to raise when an item has been added or updated.
 */
export class ItemAddedOrUpdatedEvent extends CustomEvent {
    get itemIdAsString() {
        return this.detail.itemIdAsString;
    }
    get isAdd() {
        return this.detail.isAdd;
    }
    static getEventName(sendingBackDown) {
        return `itemAddedOrUpdatedEvent_${sendingBackDown ? 'down' : 'up'}`;
    }
    // Set sendingBackDown to false when sending from the card up to the page,
    // and to true when the page sends an event back down to the list.
    constructor(itemId, isAdd, sendingBackDown = false) {
        super(ItemAddedOrUpdatedEvent.getEventName(sendingBackDown), {
            bubbles: true,
            composed: true,
            detail: new ItemAddedOrUpdatedEventDetail(itemId, isAdd)
        });
    }
}
/**
 * An event to raise when an item has been deleted.
 */
export class ItemDeletedEvent extends Event {
    static getEventName(sendingBackDown) {
        return `itemDeletedEvent_${sendingBackDown ? 'down' : 'up'}`;
    }
    // Set sendingBackDown to false when sending from the card up to the page,
    // and to true when the page sends an event back down to the list.
    constructor(sendingBackDown = false) {
        super(ItemDeletedEvent.getEventName(sendingBackDown), {
            bubbles: true,
            composed: true
        });
    }
}
class PopoverDetail {
    constructor(title, text, isSuccess = true) {
        this.title = title;
        this.text = text;
        this.isSuccess = isSuccess;
    }
    // Make this a separate property because we might have more than 2 types of popover someday.
    get isError() {
        return !this.isSuccess;
    }
}
/**
 * An event to raise when we want an informational popover to display.
 */
export class ShowPopoverEvent extends CustomEvent {
    getTitle() {
        return this.detail.title;
    }
    getText() {
        return this.detail.text;
    }
    get isSuccess() {
        return this.detail.isSuccess;
    }
    get isError() {
        return this.detail.isError;
    }
    static getEventName() {
        return 'showSuccessPopoverEvent';
    }
    constructor(title, text, isSuccess = true) {
        super(ShowPopoverEvent.getEventName(), {
            bubbles: true,
            composed: true,
            detail: new PopoverDetail(title, text, isSuccess)
        });
    }
}
//# sourceMappingURL=CorPageEntityMaintenance.js.map